import { useEffect, useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  approveUser,
  listApprovedUsers,
  listPendingUsers,
  listProjects,
  rejectUser,
  type ApprovedUser,
  type PendingUser,
  type ProjectSummary,
} from "@/lib/api";

export function AdminUsersPage() {
  const [pending, setPending] = useState<PendingUser[] | null>(null);
  const [approved, setApproved] = useState<ApprovedUser[] | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      const [p, a, pr] = await Promise.all([
        listPendingUsers(),
        listApprovedUsers(),
        listProjects(),
      ]);
      setPending(p);
      setApproved(a);
      setProjects(pr);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="container py-8 space-y-10">
      <div>
        <h1 className="text-2xl font-semibold">Users</h1>
        <p className="text-sm text-muted-foreground">
          Approve incoming requests and manage who can access which
          projects.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <section>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-lg font-medium">Pending approval</h2>
          {pending && <Badge variant="warning">{pending.length}</Badge>}
        </div>
        {!pending && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {pending && pending.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No one is waiting for approval.
          </p>
        )}
        {pending && pending.length > 0 && (
          <div className="space-y-3">
            {pending.map((u) => (
              <PendingRow
                key={u.userId}
                user={u}
                projects={projects}
                onResolved={refresh}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-lg font-medium">Approved members</h2>
          {approved && <Badge>{approved.length}</Badge>}
        </div>
        {!approved && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {approved && (
          <div className="rounded-lg border">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Approved</th>
                  <th className="px-4 py-2">Roles</th>
                </tr>
              </thead>
              <tbody>
                {approved.map((u) => (
                  <tr key={u.userId} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">
                      {u.email ?? <span className="text-muted-foreground">(no email)</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(u.approvedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {u.roles.length === 0 ? (
                        <span className="text-xs text-muted-foreground">none</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {u.roles.map((r) => (
                            <Badge key={`${r.projectId}-${r.role}`} variant="outline">
                              {r.projectId}: {r.role}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function PendingRow({
  user,
  projects,
  onResolved,
}: {
  user: PendingUser;
  projects: ProjectSummary[];
  onResolved: () => void | Promise<void>;
}) {
  const [role, setRole] = useState<"viewer" | "operator" | "admin">("viewer");
  const [projectIds, setProjectIds] = useState<string[]>(
    projects[0] ? [projects[0].id] : []
  );
  const [busy, setBusy] = useState(false);

  function toggleProject(id: string) {
    setProjectIds((curr) =>
      curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]
    );
  }

  async function approve() {
    if (projectIds.length === 0) return;
    setBusy(true);
    try {
      await approveUser(user.userId, { role, projectIds });
      await onResolved();
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    setBusy(true);
    try {
      await rejectUser(user.userId);
      await onResolved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="font-medium">{user.email ?? user.userId}</div>
          <div className="text-xs text-muted-foreground">
            requested {new Date(user.requestedAt).toLocaleString()}
          </div>
        </div>
      </div>
      {user.message && (
        <div className="mt-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
          {user.message}
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Role
          </div>
          <div className="flex gap-1">
            {(["viewer", "operator", "admin"] as const).map((r) => (
              <Button
                key={r}
                size="sm"
                variant={role === r ? "default" : "outline"}
                onClick={() => setRole(r)}
                type="button"
              >
                {r}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Projects
          </div>
          <div className="flex flex-wrap gap-1">
            {projects.map((p) => (
              <Button
                key={p.id}
                size="sm"
                variant={projectIds.includes(p.id) ? "default" : "outline"}
                onClick={() => toggleProject(p.id)}
                type="button"
              >
                {p.icon} {p.name}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={reject}
          disabled={busy}
        >
          <X className="mr-1 h-3.5 w-3.5" /> Reject
        </Button>
        <Button
          size="sm"
          onClick={approve}
          disabled={busy || projectIds.length === 0}
        >
          <Check className="mr-1 h-3.5 w-3.5" /> Approve
        </Button>
      </div>
    </div>
  );
}
